import { BaseApp } from './BaseApp';
import { AppSettingsData } from './AppSettingsData';
import { SceneSettingsData } from './SceneSettingsData';
import { SceneNodeManager } from './SceneNodeManager';
import { SystemState } from './SystemState';
import { Scene } from './Scene';
import * as THREE from 'three';

/**
 * 3DApp - 3D-specific application extending BaseApp
 * Contains all 3D engine functionality for 3D applications
 * This is what POIApp and other 3D apps should extend from
 */
export abstract class App3D<T extends SystemState = SystemState> extends BaseApp {
    // 3D-specific components
    public readonly appSettings: AppSettingsData;
    public readonly sceneSettings: SceneSettingsData;
    public sceneManager: SceneNodeManager | null = null;
    public scene: Scene | null = null;
    public raycaster: THREE.Raycaster;

    // UI-related elements
    protected canvas: HTMLCanvasElement | null = null;
    protected cssContainer: HTMLElement | null = null;
    
    // System state
    protected systemState: T;
    
    protected constructor() {
        super();
        
        // Initialize 3D-specific components using Singleton pattern
        this.appSettings = new AppSettingsData();
        AppSettingsData.setInstance(this.appSettings);
        
        this.sceneSettings = new SceneSettingsData();
        SceneSettingsData.setInstance(this.sceneSettings);
        
        this.raycaster = new THREE.Raycaster();
        
        // System state must be initialized in the subclass constructor
        // after super() has been called.
        this.systemState = new SystemState(this.eventManager) as T;
        
        // Setup 3D-specific event handlers
        this.setup3DEventHandlers();
    }
    
    /**
     * Attaches the necessary UI elements to the app.
     * This must be called before initialization.
     * @param canvas The canvas element for WebGL rendering.
     * @param cssContainer The container for CSS3D rendering.
     */
    public attachUI(canvas: HTMLCanvasElement, cssContainer: HTMLElement): void {
        this.canvas = canvas;
        this.cssContainer = cssContainer;
    }
    
    /**
     * Initialize 3D application
     */
    public async initialize(): Promise<void> {
        if (!this.canvas || !this.cssContainer) {
            throw new Error('UI elements not attached. Call attachUI() before initializing.');
        }

        this.systemState.setStatus('initializing');
        
        try {
            // Initialize the scene with both settings
            this.scene = new Scene(this.canvas, this.cssContainer, this.sceneSettings, this.appSettings);

            // Initialize scene manager
            this.sceneManager = new SceneNodeManager(this.scene);
            await this.sceneManager.initialize();
            
            // Call base initialization (which calls onInitialize)
            await super.initialize();
            
            this.systemState.setStatus('running'); // Or 'initialized' if not auto-playing
            
        } catch (error) {
            const errorMessage = `3D initialization failed: ${error instanceof Error ? error.message : String(error)}`;
            this.systemState.addError(errorMessage);
            throw error;
        }
    }
    
    /**
     * Starts the application and the main update loop.
     * This method should be called by the UI or application entry point.
     */
    public start(): void {
        if (this.isRunning()) {
            console.warn("App is already running.");
            return;
        }
        this.onStart();
    }
    
    /**
     * Start 3D application with update loop
     */
    protected onStart(): void {
        this.systemState.setStatus('running');
        this.eventManager.emit('playback:started');
        this.startUpdateLoop();
    }
    
    /**
     * Stop 3D application
     */
    protected onStop(): void {
        this.systemState.setStatus('stopped');
        this.eventManager.emit('playback:stopped');
    }
    
    /**
     * Play/resume 3D application
     */
    public play(): void {
        if (this.systemState.status === 'uninitialized' || this.systemState.status === 'initializing') {
            throw new Error('3D App must be initialized before playing');
        }
        
        if (!this.isRunning()) {
            this.start();
        } else if (this.systemState.isPaused()) {
            this.resume();
        }
    }
    
    /**
     * Pause the 3D application
     */
    public pause(): void {
        if (!this.isRunning()) return;
        
        this.systemState.setStatus('paused');
        this.eventManager.emit('playback:paused');
    }
    
    /**
     * Resume the 3D application
     */
    public resume(): void {
        if (!this.isRunning() || !this.systemState.isPaused()) return;
        
        this.systemState.setStatus('running');
        this.eventManager.emit('playback:resumed');
    }
    
    /**
     * Get current 3D system state
     */
    public getSystemState(): { status: string, errors: string[] } {
        return {
            status: this.systemState.status,
            errors: this.systemState.errorMessages,
        };
    }
    
    /**
     * Add error to 3D system state
     */
    protected addError(message: string): void {
        this.systemState.addError(message);
        this.eventManager.emit('system:error', new Error(message));
    }
    
    /**
     * Clear all errors
     */
    protected clearErrors(): void {
        if (this.systemState.hasError()) {
            this.systemState.setStatus('running'); // Or whatever the default state is
        }
    }
    
    /**
     * Dispose 3D resources
     */
    public dispose(): void {
        this.scene?.dispose();
        this.sceneManager?.dispose();
        super.dispose();
    }
    
    /**
     * Abstract method for 3D-specific update logic
     */
    protected abstract onUpdate(deltaTime: number): void;
    
    // Private methods
    private setup3DEventHandlers(): void {
        // Handle scene changes
        this.sceneManager?.onSceneChanged(() => {
            this.eventManager.emit('scene:changed');
        });
        
        // 🎯 NO MORE EVENT FORWARDING! 
        // Components should listen directly to properties: property.on(handler)
        // Instead of: app.eventManager.on('settings:changed', handler)
    }
    
    private startUpdateLoop(): void {
        let lastUpdateTime = performance.now();
        const update = (currentTime: number) => {
            if (this.systemState.status === 'stopped') return;
            
            requestAnimationFrame(update);

            const deltaTime = currentTime - lastUpdateTime;
            lastUpdateTime = currentTime;
            
            if (this.systemState.isRunning()) {
                // Update scene
                this.sceneManager?.update(deltaTime / 1000); // Convert to seconds
                
                // Update and render the main scene
                this.scene?.updateAndRender(deltaTime / 1000);

                // Call subclass update
                this.onUpdate(deltaTime / 1000);
                
                // Emit update event
                this.eventManager.emit('system:update', { 
                    deltaTime: deltaTime / 1000
                });
            }
        };
        requestAnimationFrame(update);
    }
} 